import { useState, useEffect } from 'react';
import api from '../api';
import Layout from '../components/Layout';
import { useToast } from '../components/ToastContext';
import EbayConnect from '../components/EbayConnect';
import ProGate from '../components/ProGate';
import { useIsMobile } from '../hooks/useIsMobile';
import { T } from '../styles/theme';

export default function Annunci() {
  const toast = useToast();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('ebay');
  const [ebayConnected, setEbayConnected] = useState(false);
  const [ebayItems, setEbayItems] = useState([]);
  const [vintedItems, setVintedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [filter, setFilter] = useState('all');
  const [editModal, setEditModal] = useState(null); // {item_id, price}
  const [newPrice, setNewPrice] = useState('');
  const [saving, setSaving] = useState(false);

  // Check eBay status
  useEffect(() => {
    api.get('/auth/ebay-status').then(r => setEbayConnected(r.data?.collegato)).catch(() => {});
  }, []);

  // Load data based on tab
  useEffect(() => {
    if (tab === 'ebay' && ebayConnected) syncEbay();
    if (tab === 'vinted') loadVinted();
  }, [tab, ebayConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncEbay = async (force = false) => {
    setLoading(true);
    try {
      const r = await api.get(`/annunci/ebay/sync${force ? '?force=true' : ''}`);
      setEbayItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setLastSync(r.data?.last_sync);
    } catch { toast.error('Errore sincronizzazione eBay'); }
    finally { setLoading(false); }
  };

  const loadVinted = async () => {
    setLoading(true);
    try {
      const r = await api.get('/annunci/vinted/lista');
      setVintedItems(Array.isArray(r.data) ? r.data : []);
    } catch {}
    finally { setLoading(false); }
  };

  const handleEditPrice = async () => {
    if (!editModal || !newPrice) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('prezzo', parseFloat(newPrice));
      await api.put(`/annunci/ebay/${editModal.item_id}/prezzo`, fd);
      toast.success('Prezzo aggiornato!');
      setEditModal(null);
      setNewPrice('');
      syncEbay();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore aggiornamento prezzo'); }
    finally { setSaving(false); }
  };

  const handleDeleteEbay = async (itemId) => {
    if (!window.confirm('Vuoi terminare questo annuncio su eBay?')) return;
    try {
      await api.delete(`/annunci/ebay/${itemId}`);
      // Elimina anche dal DB locale per evitare che ricompaia al sync
      try { await api.post('/annunci/ebay/remove-local', { item_id: itemId }); } catch {}
      toast.success('Annuncio terminato');
      setEbayItems(prev => prev.filter(i => i.item_id !== itemId));
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDeleteVinted = async (id) => {
    if (!window.confirm('Rimuovere dal tracciamento?')) return;
    try {
      await api.delete(`/annunci/vinted/lista/${id}`);
      setVintedItems(prev => prev.filter(i => i.id !== id));
      toast.success('Rimosso dal tracciamento');
    } catch { toast.error('Errore'); }
  };

  const filteredEbay = filter === 'all' ? ebayItems : ebayItems.filter(i => i.status === filter);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '';

  return (
    <Layout>
      <ProGate feature="Annunci">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h1 className="page-title">I tuoi Annunci</h1>
          <p style={{ color: T.muted, fontFamily: T.fontBody, fontSize: '0.85rem', marginTop: 4 }}>
            Gestisci tutto quello che hai pubblicato su eBay e Vinted
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {[
            { key: 'ebay', label: 'eBay', count: ebayItems.length },
            { key: 'vinted', label: 'Vinted', count: vintedItems.length },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '10px 24px', borderRadius: 12, border: 'none', cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: 700, fontFamily: T.fontBody,
              background: tab === t.key ? T.gradPrimary : T.elem,
              color: tab === t.key ? '#fff' : T.muted,
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {t.key === 'ebay' ? '🛒' : '👗'} {t.label}
              {t.count > 0 && (
                <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
                  background: tab === t.key ? 'rgba(255,255,255,0.2)' : T.border, color: tab === t.key ? '#fff' : T.violet }}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* === TAB EBAY === */}
        {tab === 'ebay' && (
          <>
            {!ebayConnected ? (
              <div className="card" style={{ padding: 32, textAlign: 'center', borderRadius: 20 }}>
                <p style={{ color: T.text, fontSize: '1.1rem', fontWeight: 700, fontFamily: T.fontDisplay, marginBottom: 8 }}>
                  Collega il tuo account eBay
                </p>
                <p style={{ color: T.muted, fontSize: '0.85rem', marginBottom: 20 }}>
                  Per vedere e gestire i tuoi annunci eBay
                </p>
                <EbayConnect onStatusChange={(s) => setEbayConnected(s?.collegato)} />
              </div>
            ) : (
              <>
                {/* Sync bar */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {lastSync && (
                      <span style={{ color: T.muted, fontSize: '0.75rem', fontFamily: T.fontBody }}>
                        Ultimo sync: {new Date(lastSync.endsWith('Z') ? lastSync : lastSync + 'Z').toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', timeZone: 'Europe/Rome' })}
                      </span>
                    )}
                    <button onClick={() => syncEbay(true)} disabled={loading} style={{
                      padding: '6px 16px', borderRadius: 10, border: `1px solid ${T.border}`, background: 'transparent',
                      color: T.violet, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: T.fontBody,
                    }}>
                      {loading ? 'Sincronizzazione...' : 'Sincronizza'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {['all', 'Active', 'Ended'].map(f => (
                      <button key={f} onClick={() => setFilter(f)} style={{
                        padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                        fontSize: '0.75rem', fontWeight: 600,
                        background: filter === f ? T.violetDark : 'transparent',
                        color: filter === f ? '#fff' : T.muted,
                      }}>
                        {f === 'all' ? 'Tutti' : f === 'Active' ? 'Attivi' : 'Scaduti'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* eBay items grid */}
                {loading ? (
                  <div style={{ textAlign: 'center', padding: 40, color: T.muted }}>Sincronizzazione annunci eBay...</div>
                ) : filteredEbay.length === 0 ? (
                  <div className="card" style={{ padding: 40, textAlign: 'center', borderRadius: 20 }}>
                    <p style={{ fontSize: '2rem', marginBottom: 8 }}>📦</p>
                    <p style={{ color: T.muted }}>Nessun annuncio eBay trovato</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16 }}>
                    {filteredEbay.map(item => (
                      <div key={item.item_id || item.id} className="card" style={{ borderRadius: 16, overflow: 'hidden', transition: 'all 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = T.violetDark}
                        onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                        <div style={{ display: 'flex', gap: 14, padding: 16 }}>
                          {/* Photo */}
                          <div style={{ width: 80, height: 80, borderRadius: 12, overflow: 'hidden', flexShrink: 0, background: T.elem }}>
                            {item.photo_url ? (
                              <img src={item.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', opacity: 0.3 }}>📦</div>
                            )}
                          </div>
                          {/* Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 8 }}>
                              <p style={{ color: T.text, fontWeight: 600, fontSize: '0.88rem', fontFamily: T.fontBody, margin: 0,
                                overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                {item.title}
                              </p>
                              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.65rem', fontWeight: 700, flexShrink: 0,
                                background: item.status === 'Active' ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.12)',
                                color: item.status === 'Active' ? T.green : T.red,
                                border: `1px solid ${item.status === 'Active' ? 'rgba(74,222,128,0.25)' : 'rgba(248,113,113,0.25)'}`,
                              }}>
                                {item.status === 'Active' ? 'Attivo' : 'Scaduto'}
                              </span>
                            </div>
                            <p style={{ color: T.violet, fontWeight: 800, fontSize: '1.2rem', fontFamily: T.fontBody, margin: '6px 0 4px' }}>
                              {'\u20AC'}{(item.price || 0).toFixed ? item.price.toFixed(2) : item.price}
                            </p>
                            <div style={{ display: 'flex', gap: 12, fontSize: '0.75rem', color: T.muted }}>
                              {item.view_count > 0 && <span title="Visualizzazioni">👁 {item.view_count} visualizzazioni</span>}
                              <span title="Osservatori">⭐ {item.watcher_count || 0} osservatori</span>
                              {(item.best_offer_count > 0 || item.quantity_sold > 0) && <span>📦 {item.quantity_sold || 0} venduti</span>}
                            </div>
                          </div>
                        </div>
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 6, padding: '0 16px 14px', flexWrap: 'wrap', alignItems: 'center' }}>
                          {item.listing_url && (
                            <a href={item.listing_url} target="_blank" rel="noopener noreferrer" style={{
                              padding: '6px 14px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600,
                              background: T.elem, border: `1px solid ${T.border}`, color: T.text,
                              textDecoration: 'none', fontFamily: T.fontBody,
                            }}>
                              Vedi su eBay
                            </a>
                          )}
                          <button onClick={() => { setEditModal(item); setNewPrice(item.price || ''); }} style={{
                            padding: '6px 14px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600,
                            background: 'rgba(167,139,250,0.1)', border: `1px solid ${T.border}`, color: T.violet,
                            cursor: 'pointer', fontFamily: T.fontBody,
                          }}>
                            Modifica prezzo
                          </button>
                          <button onClick={() => handleDeleteEbay(item.item_id)} style={{
                            padding: '6px 14px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600,
                            background: 'rgba(248,113,113,0.08)', border: `1px solid rgba(248,113,113,0.2)`, color: T.red,
                            cursor: 'pointer', fontFamily: T.fontBody,
                          }}>
                            Termina
                          </button>
                          <button onClick={() => handleDeleteEbay(item.item_id)} style={{
                            marginLeft: 'auto', width: 32, height: 32, borderRadius: 8,
                            background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', transition: '0.2s', flexShrink: 0,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.35)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.15)'; }}
                          title="Elimina annuncio">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill={T.red} stroke="none"><path d="M3 6h18v2H3V6zm2 3h14l-1.2 12.5a2 2 0 01-2 1.5H8.2a2 2 0 01-2-1.5L5 9zm4 2v8h2v-8H9zm4 0v8h2v-8h-2zM8 4h8v2H8V4z"/></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* === TAB VINTED === */}
        {tab === 'vinted' && (
          <>
            <div className="card" style={{ padding: 16, borderRadius: 14, marginBottom: 20, background: 'rgba(167,139,250,0.05)', border: `1px solid ${T.border}` }}>
              <p style={{ color: T.text, fontSize: '0.82rem', fontFamily: T.fontBody, margin: 0 }}>
                Gli annunci Vinted vengono tracciati automaticamente ogni volta che pubblichi tramite l'estensione Chrome. Per modificarli vai direttamente su Vinted.
              </p>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: T.muted }}>Caricamento...</div>
            ) : vintedItems.length === 0 ? (
              <div className="card" style={{ padding: 48, textAlign: 'center', borderRadius: 20 }}>
                <p style={{ fontSize: '2.5rem', marginBottom: 12 }}>👗</p>
                <p style={{ color: T.text, fontWeight: 700, fontSize: '1.05rem', fontFamily: T.fontDisplay, marginBottom: 6 }}>
                  Nessun annuncio Vinted tracciato
                </p>
                <p style={{ color: T.muted, fontSize: '0.82rem', maxWidth: 360, margin: '0 auto' }}>
                  Pubblica il tuo primo annuncio su Vinted tramite l'estensione Chrome — apparira automaticamente qui
                </p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16 }}>
                {vintedItems.map(item => (
                  <div key={item.id} className="card" style={{ borderRadius: 16, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', gap: 14, padding: 16 }}>
                      <div style={{ width: 72, height: 72, borderRadius: 12, overflow: 'hidden', flexShrink: 0, background: T.elem }}>
                        {item.photo_url ? (
                          <img src={item.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', opacity: 0.3 }}>👗</div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ color: T.text, fontWeight: 600, fontSize: '0.88rem', fontFamily: T.fontBody, margin: '0 0 4px',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.title}
                        </p>
                        <p style={{ color: T.violet, fontWeight: 800, fontSize: '1.1rem', margin: '4px 0' }}>
                          {'\u20AC'}{(item.price || 0).toFixed ? item.price.toFixed(2) : item.price}
                        </p>
                        <p style={{ color: T.muted, fontSize: '0.7rem', margin: 0 }}>{formatDate(item.created_at)}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, padding: '0 16px 14px', alignItems: 'center' }}>
                      {item.url_annuncio ? (
                        <a href={item.url_annuncio} target="_blank" rel="noopener noreferrer" style={{
                          padding: '6px 14px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600,
                          background: T.elem, border: `1px solid ${T.border}`, color: T.text, textDecoration: 'none',
                        }}>
                          Vedi su Vinted
                        </a>
                      ) : (
                        <span style={{ padding: '6px 14px', borderRadius: 10, fontSize: '0.75rem',
                          background: T.elem, border: `1px solid ${T.border}`, color: T.faint, opacity: 0.5 }}>
                          Link non disponibile
                        </span>
                      )}
                      <button onClick={() => handleDeleteVinted(item.id)} style={{
                        padding: '6px 14px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600,
                        background: 'rgba(248,113,113,0.08)', border: `1px solid rgba(248,113,113,0.2)`, color: T.red,
                        cursor: 'pointer',
                      }}>
                        Rimuovi
                      </button>
                      <button onClick={() => handleDeleteVinted(item.id)} style={{
                        marginLeft: 'auto', width: 32, height: 32, borderRadius: 8,
                        background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', transition: '0.2s', flexShrink: 0,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.35)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.15)'; }}
                      title="Elimina annuncio">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill={T.red} stroke="none"><path d="M3 6h18v2H3V6zm2 3h14l-1.2 12.5a2 2 0 01-2 1.5H8.2a2 2 0 01-2-1.5L5 9zm4 2v8h2v-8H9zm4 0v8h2v-8h-2zM8 4h8v2H8V4z"/></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Edit Price Modal */}
        {editModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(13,11,30,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={(e) => e.target === e.currentTarget && setEditModal(null)}>
            <div className="card" style={{ padding: 28, borderRadius: 20, maxWidth: 400, width: '100%' }}>
              <h3 style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: '1.1rem', fontWeight: 700, marginBottom: 4 }}>
                Modifica prezzo
              </h3>
              <p style={{ color: T.muted, fontSize: '0.8rem', marginBottom: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {editModal.title}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                <span style={{ color: T.violet, fontSize: '1.3rem', fontWeight: 800 }}>{'\u20AC'}</span>
                <input type="number" step="0.01" value={newPrice} onChange={e => setNewPrice(e.target.value)}
                  className="input" style={{ flex: 1, fontSize: '1.2rem', fontWeight: 700, textAlign: 'center' }}
                  autoFocus />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setEditModal(null)} className="btn-secondary" style={{ flex: 1, padding: '10px 0', textAlign: 'center' }}>
                  Annulla
                </button>
                <button onClick={handleEditPrice} disabled={saving} className="btn-primary" style={{ flex: 1, padding: '10px 0', textAlign: 'center' }}>
                  {saving ? 'Salvataggio...' : 'Salva prezzo'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      </ProGate>
    </Layout>
  );
}
